import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { Sparkles, ArrowRight, X, Flame } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSoloAnnualPromo } from '../hooks/useSoloAnnualPromo';

export const AnimatedBadge = ({ children, className = "" }) => (
  <div className={`relative inline-flex group ${className}`}>
    <span className="relative inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-primary/20 via-pink-500/15 to-primary/20 border-2 border-primary/60 rounded-full text-base font-bold overflow-hidden transition-all duration-500 hover:scale-105 hover:border-primary">
      {/* Animated shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer" />
      <Sparkles className="w-5 h-5 text-primary animate-bounce-slow" />
      <span className="text-primary font-bold">{children}</span>
      <Sparkles className="w-5 h-5 text-pink-400 animate-bounce-slow" style={{ animationDelay: '0.3s' }} />
    </span>
  </div>
);

// Section Badge Component - Large & Visible WAHOU Style (Clean & Dynamic)

export const SectionBadge = ({ children, emoji = "", className = "", compact = false }) => (
  <div className={`relative inline-flex ${compact ? 'mb-2 sm:mb-6' : 'mb-6'} group ${className}`}>
    <span className={`relative inline-flex items-center overflow-hidden rounded-full border-2 border-primary/50 bg-gradient-to-r from-primary/15 via-pink-500/10 to-primary/15 font-bold transition-all duration-500 hover:scale-105 hover:border-primary ${compact ? 'gap-1.5 sm:gap-3 px-4 sm:px-8 py-1.5 sm:py-4 text-xs sm:text-lg' : 'gap-3 px-8 py-4 text-lg'}`}>
      {/* Animated shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full animate-shimmer" />
      {emoji && <span className={compact ? 'text-base sm:text-2xl animate-bounce-slow' : 'text-2xl animate-bounce-slow'}>{emoji}</span>}
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-400 to-primary font-bold">{children}</span>
      <div className={`bg-gradient-to-r from-primary to-pink-500 rounded-full animate-pulse ${compact ? 'w-1.5 h-1.5 sm:w-2.5 sm:h-2.5' : 'w-2.5 h-2.5'}`} />
    </span>
  </div>
);

// Floating particles background

export const ParticlesBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-primary/30 rounded-full animate-float"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 5}s`,
          animationDuration: `${3 + Math.random() * 4}s`
        }}
      />
    ))}
  </div>
);

// Compte à rebours compact et réutilisable (bannière, /offres, in-app, accueil) — se
// met à jour chaque minute, se rend simplement "Terminé" côté affichage si la date est
// dépassée (le backend, source de vérité, aura de toute façon déjà désactivé la promo).
export const PromoCountdown = ({ endsAt, language = 'fr', compact = false }) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!endsAt) return;
    const end = new Date(endsAt).getTime();
    const tick = () => {
      const diff = end - Date.now();
      if (diff <= 0) { setLeft({ d: 0, h: 0, m: 0 }); return; }
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
      });
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (!left) return null;
  const isFr = language === 'fr';
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono font-bold ${compact ? 'text-[11px]' : 'text-sm'}`} data-testid="promo-countdown">
      <Flame className={compact ? 'w-3 h-3 text-orange-400' : 'w-4 h-4 text-orange-400'} />
      {isFr ? `${left.d}j ${left.h}h ${left.m}min restantes` : `${left.d}d ${left.h}h ${left.m}min left`}
    </span>
  );
};

// Bandeau "essai gratuit" — slim, dismissible, persiste via localStorage. Texte/lien
// personnalisables par l'admin (GET /api/settings/banner, panneau "Réglages du site").
// Priorité automatique à la promo -50% Suivi Pro+ annuel quand elle est active (source de
// vérité backend, aucune action admin requise, disparaît seule après le 31 octobre 2026).
export const AppBadgeBanner = () => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('body_up_trial_banner_dismissed') === '1'; } catch { return false; }
  });
  const [cfg, setCfg] = useState(null);
  const promo = useSoloAnnualPromo();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/banner`)
      .then((r) => r.json())
      .then(setCfg)
      .catch(() => {});
  }, []);

  if (dismissed || (cfg && cfg.enabled === false)) return null;
  const dismiss = () => {
    try { localStorage.setItem('body_up_trial_banner_dismissed', '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  if (promo?.active) {
    return (
      <div className="bg-gradient-to-r from-orange-600 via-red-500 to-pink-600 text-white" data-testid="trial-banner">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-9 flex items-center justify-center gap-2 sm:gap-3 relative">
          <a href="/offres" className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs font-bold tracking-tight hover:underline text-center truncate" data-testid="trial-banner-link">
            <span>🔥</span>
            <span className="truncate">
              {isFr
                ? `Suivi Pro+ : -${promo.discountPct}% sur l'annuel (${promo.price}.- au lieu de ${promo.originalPrice}.-) + essai 7j gratuit`
                : `Suivi Pro+: -${promo.discountPct}% annual (CHF ${promo.price} instead of ${promo.originalPrice}) + 7-day free trial`}
            </span>
            <span className="hidden sm:inline"><PromoCountdown endsAt={promo.endsAt} language={language} compact /></span>
          </a>
          <button onClick={dismiss} aria-label={isFr ? 'Fermer' : 'Close'} className="absolute right-2 sm:right-4 p-1 rounded-full hover:bg-white/20 transition-colors" data-testid="trial-banner-close">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  const emoji = cfg?.emoji || '🎁';
  const text = (isFr ? cfg?.text_fr : cfg?.text_en) || (isFr
    ? 'Essai gratuit 7 jours sur Suivi Pro & Suivi Pro+ — sans engagement'
    : '7-day free trial on Suivi Pro & Suivi Pro+ — no commitment');
  const link = cfg?.link || '/offres';
  return (
    <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-white" data-testid="trial-banner">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-9 flex items-center justify-center gap-2 relative">
        <a
          href={link}
          className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold tracking-tight hover:underline text-center truncate"
          data-testid="trial-banner-link"
        >
          <span>{emoji}</span>
          <span className="truncate">{text}</span>
          <span className="hidden sm:inline underline">{isFr ? "J'en profite →" : 'Try it now →'}</span>
        </a>
        <button
          onClick={dismiss}
          aria-label={isFr ? 'Fermer' : 'Close'}
          className="absolute right-2 sm:right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
          data-testid="trial-banner-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Carte promo "effet wahou" -50% Suivi Pro+ annuel — réutilisée sur /offres, l'onglet
// Abonnement in-app et la page d'accueil. Ne s'affiche que si `promo.active` (le backend
// gère seul l'expiration au 31 octobre 2026, rien à retirer manuellement ensuite).
export const SoloAnnualPromoCard = ({ promo, language = 'fr', ctaHref = '/offres', compact = false }) => {
  const isFr = language === 'fr';
  if (!promo?.active) return null;
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-orange-500/40 bg-gradient-to-br from-orange-600/20 via-pink-600/15 to-black ${compact ? 'p-4' : 'p-5 sm:p-6'} flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6`}
      data-testid="solo-annual-promo-card"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-orange-500/25 rounded-full blur-3xl" aria-hidden="true" />
      <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center shrink-0 relative z-10">
        <Flame className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 relative z-10">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white">
            -{promo.discountPct}%
          </span>
          <h3 className="font-black text-white text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isFr ? 'Suivi Pro+ annuel en promo' : 'Suivi Pro+ annual on promo'}
          </h3>
        </div>
        <p className="text-sm text-zinc-300 mb-1.5">
          {isFr
            ? <>Au lieu de <span className="line-through text-zinc-500">{promo.originalPrice}.- CHF</span>, seulement <span className="font-black text-white">{promo.price}.- CHF/an</span> · essai 7 jours gratuit inclus</>
            : <>Instead of <span className="line-through text-zinc-500">CHF {promo.originalPrice}</span>, only <span className="font-black text-white">CHF {promo.price}/yr</span> · 7-day free trial included</>}
        </p>
        <PromoCountdown endsAt={promo.endsAt} language={language} />
      </div>
      <a
        href={ctaHref}
        className="shrink-0 self-stretch sm:self-center inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-pink-600 hover:scale-[1.03] transition-transform text-sm relative z-10"
        data-testid="solo-annual-promo-cta"
      >
        {isFr ? "J'en profite" : 'Get it now'} <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
};


export const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  
  useEffect(() => {
    // Only scroll to top on PUSH navigation (new page visit)
    // Skip on POP (back/forward) to preserve scroll position
    if (navigationType === 'POP') return;
    
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    scrollToTop();
    const timer = setTimeout(scrollToTop, 50);
    
    return () => clearTimeout(timer);
  }, [pathname, navigationType]);
  
  return null;
};

// Back navigation component for all subpages

export const BackNav = ({ label }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const defaultLabel = language === 'fr' ? 'Retour' : 'Back';
  return (
    <button
      onClick={() => navigate(-1)}
      className="inline-flex items-center gap-2 text-gray-400 hover:text-primary mb-6 transition-colors group"
      data-testid="back-nav-btn"
    >
      <ArrowRight className="w-4 h-4 rotate-180 group-hover:-translate-x-1 transition-transform" />
      <span className="text-sm font-medium">{label || defaultLabel}</span>
    </button>
  );
};

// Animation hook

export const useIntersectionObserver = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isVisible];
};

// Animated Section Component

export const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const [ref, isVisible] = useIntersectionObserver();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Service Card Component - Entire card is clickable
